import Vue from 'vue';
import { cloneDeep, findIndex } from 'lodash-es';

import Screens from '../../../services/screens';
import toast from '../../../components/toast/toast';

const state = {
  screens: [],
  connected_users: [],
  productionScreens: [],
  otherScreens: [],
  connectedScreens: [],
  screensReady: false,
  linkedScreen: false,

  // Split screens by owner for the dashboard.
  // Since productions could belong to various teams.
  isLoading: {},
  byOwner: {},
  screenMap: {},
};

const actions = {
  socket_connectedScreens(context, payload) {
    context.commit('SET_SOCKET_SCREENS', payload);
  },

  socket_connectedScreensByProduction(context, payload) {
    // Only update the connected screens for the provided production that changed
    context.commit('MERGE_SCREEN_MAP', {
      [payload.productionId]: payload.screens,
    });
  },

  /**
   * Called when a new screen is successfully generated via the socket server.
   * This will finalize creation via `tgb-live` API and set the linked screen in state.
   */
  async socket_link(context, payload) {
    const screen = await context.dispatch('createScreen', payload);
    context.dispatch('setLinkedScreen', screen);
  },

  setLinkedScreen(context, payload) {
    context.commit('SET_LINKED_SCREEN', payload);
  },

  connectScreen(context, payload) {
    vm.$socket.emit('connectScreen', { ...payload });
  },

  generateScreen(context, payload) {
    vm.$socket.emit('generateScreen', payload);
  },

  /**
   * Send socket message to tgb-sockets requesting list of connected screens
   * Will fetch list of all connected screens by production.
   * Also functions as a way to subscribe to future changes,
   * so we don't need to call this again for the same productions.
   * @param {Object} context
   * @param {Number} [productionId] - ID of production to fetch screens for.
   *   If not provided, then will pull all productionIds from root state.
   */
  checkConnectedScreens(context, productionId) {
    const opts = {};

    if (productionId) {
      opts.production_ids = [productionId];
    } else {
      // To cut down on payload size, we'll just get what we are missing
      const loadedIds = Object.keys(context.state.screenMap).map((id) => parseInt(id, 10));
      const productionIds = context.rootState.production.productions
        .map((p) => p._id)
        .filter((id) => !loadedIds.includes(id));

      opts.production_ids = productionIds;
    }

    vm.$socket.emit('checkConnectedScreens', opts, (data) => {
      context.commit('MERGE_SCREEN_MAP', data.screens);
    });
  },

  /**
   * Get list of screens for the provided owner.
   * @param {Array<Number>} owner - ID of the owner to fetch list of screens for.
   */
  async getScreens(context, owner) {
    // If owner isn't provided, use whatever the owner of the current production is.
    if (!owner) {
      const { production } = context.rootState.production;
      owner = production?.owner; // eslint-disable-line no-param-reassign
    }

    // No provided owner, and no production loaded in state, so just leave
    if (!owner) { return; }

    // If screens already loaded for the owner, then skip for now
    // This means any new screens won't show up without a hard reload.
    if (context.state.byOwner[owner]) { return; }

    context.commit('SET_SCREENS_LOADING', { owner, isLoading: true });

    try {
      const { screens } = await Screens.getScreens();

      context.commit('SET_SCREENS', {
        screens,
        productionId: context.rootState.production.production._id,
        owner,
      });
    } finally {
      context.commit('SET_SCREENS_LOADING', { owner, isLoading: false });
    }
  },

  async createScreen(context, screen) {
    await Screens.createScreen(screen)
      .catch((err) => {
        console.error('Failed to create new screen:', err);

        toast.makeToast({
          heading: 'Error',
          icon: 'error',
          loaderBg: 'red',
          text: 'Failed to create new screen.',
        });

        return null;
      });

    context.commit('ADD_SCREEN', screen);
    return screen;
  },

  async editScreen(context, payload) {
    await Screens.editScreen(payload._id, payload);
    context.commit('EDITED_SCREEN', payload);
  },

  async deleteScreen(context, payload) {
    await Screens.deleteScreen(payload._id);
    context.commit('DELETE_SCREEN', payload);
  },

  async changeScreenProductionId(context, id) {
    const index = findIndex(state.screens, (o) => o._id === id);
    const clone = cloneDeep(state.screens[index]);

    clone.production_id = context.rootState.production.production._id;

    await Screens.editScreen(id, clone);

    vm.$socket.emit('disconnectScreen', { screen_id: id, production_id: state.screens[index].production_id });
    vm.$socket.emit('connectScreen', { screen_id: id, production_id: clone.production_id });

    clone.production = context.rootState.production.production;
    context.commit('EDITED_SCREEN', clone);
  },

  async removeScreenProductionId(context, id) {
    const index = findIndex(state.screens, (o) => o._id === id);
    const clone = cloneDeep(state.screens[index]);
    clone.production_id = null;
    await Screens.editScreen(id, clone);
    context.commit('EDITED_SCREEN', clone);
    vm.$socket.emit('disconnectScreen', { screen_id: id, production_id: context.rootState.production.production._id });
  },

  setScreens(context) {
    context.commit('CHECK_SCREEN_ONLINE');
  },
};

const mutations = {
  /* eslint-disable no-param-reassign, no-shadow */

  SET_SCREENS_LOADING(state, { owner, isLoading }) {
    Vue.set(state.isLoading, owner, isLoading);
  },

  SET_LINKED_SCREEN(state, screen) {
    state.linkedScreen = screen;
  },

  ADD_SCREEN(state, payload) {
    state.screens.push({
      ...payload,
      _id: payload.screen_id,
      status: 'disconnected',
    });
  },

  MERGE_SCREEN_MAP(state, screenMap) {
    state.screenMap = {
      ...state.screenMap,
      ...screenMap,
    };
  },

  SET_SCREENS(state, payload) {
    const { owner, productionId, screens } = payload;

    // Store ALL screens
    state.screens = [...state.screens, ...screens];

    // Store screens specific to the current production if one is open
    if (productionId) {
      state.productionScreens = screens.filter((s) => s.production_id === productionId);
      state.otherScreens = screens.filter((s) => s.production_id !== productionId);
    }

    if (owner) {
      state.byOwner[owner] = screens;
    }
  },

  SET_SOCKET_SCREENS(state, payload) {
    state.connectedScreens = payload.screens;
    state.screensReady = true;

    // Update status on screens and productionScreens
    // (I think productionScreens might not be used, but still)
    state.screens = state.screens.map((screen) => {
      const isConnected = !!state.connectedScreens.find((s) => s.screen_id === screen._id);

      return {
        ...screen,
        status: isConnected ? 'connected' : 'disconnected',
      };
    });

    state.productionScreens = state.productionScreens.map((screen) => {
      const isConnected = !!state.connectedScreens.find((s) => s.screen_id === screen._id);

      return {
        ...screen,
        status: isConnected ? 'connected' : 'disconnected',
      };
    });
  },

  EDITED_SCREEN(state, payload) {
    const index = findIndex(state.screens, (o) => o._id === payload._id);

    if (!payload.production_id) {
      delete payload.production;
    }

    state.screens[index] = payload;
  },

  DELETE_SCREEN(state, payload) {
    const index = findIndex(state.screens, (o) => o._id === payload._id);
    state.screens.splice(index, 1);
    vm.$socket.emit('clearScreen', { screen_id: payload._id });

    // If we're a generated screen, remove the verification code from cache
    if (payload.token) {
      vm.$socket.emit('removeGeneratedScreen', { token: payload.token });
    }
  },

  CHECK_SCREEN_ONLINE(state) {
    const screensClone = [...state.screens];
    const connectedScreensClone = [...state.connectedScreens];
    screensClone.forEach((client) => {
      const index = findIndex(connectedScreensClone, (o) => o.screen_id === client._id);
      client.status = index === -1 ? 'disconnected' : 'connected';
    });

    state.screens = screensClone;
    state.screensReady = false;
  },
};

const getters = {
  /**
   * Get list of screens by production ID
   * @param {Number} id - Production ID.
   * @returns {Array<Object>} List of screens assigned to production.
   */
  getScreensByProduction: (state) => (id) => {
    const { screenMap, screens } = state;
    const connectedScreens = screenMap[id] || [];

    return screens
      .filter((screen) => screen.production_id === id)
      .map((screen) => ({
        ...screen,
        status: (
          connectedScreens.some((s) => screen._id === s.screen_id)
            ? 'connected'
            : 'disconnected'
        ),
      }));
  },

  /**
   * Returns list of screens attached to the current active production.
   * Will also set the `status` field on the screens showing if they are connected (open) or not
   */
  productionScreens(state, getters, rootState) {
    const { connectedScreens, screens } = state;
    const { _id: productionId, owner } = rootState.production.production;

    return screens
      .filter((screen) => (
        screen.owner === owner
        && screen.production_id === productionId
      ))
      .map((screen) => ({
        ...screen,
        status: (
          connectedScreens.some((s) => screen._id === s.screen_id)
            ? 'connected'
            : 'disconnected'
        ),
      }));
  },

  /**
   * Returns list of screens NOT attached to the current active production.
   * Will also set the `status` field on the screens showing if they are connected (open) or not
   */
  otherScreens(state, getters, rootState) {
    const { connectedScreens, screens } = state;
    const { _id: productionId, owner } = rootState.production.production;

    return screens
      .filter((screen) => (
        screen.owner === owner
        && screen.production_id !== productionId
      ))
      .map((screen) => ({
        ...screen,
        status: (
          connectedScreens.some((s) => screen._id === s.screen_id)
            ? 'connected'
            : 'disconnected'
        ),
      }));
  },

  /**
   * Determines if there are any connected screens (live outputs) for the current production.
   * A connected screen is any output used by the production that has at least one viewer.
   *
   * @function
   * @param {Object} state - The state object.
   * @param {Array} state.connectedScreens - An array representing the connected screens.
   * @returns {boolean} Returns true if there are connected screens, otherwise false.
   */
  hasConnectedScreens: (state) => !!state?.connectedScreens?.length,

  /**
   * The number of connected screens for the current production.
   *
   * @param {Object} state
   * @returns {number}
   */
  connectedScreensCount: (state) => state?.connectedScreens?.length || 0,
};

export default {
  state,
  actions,
  mutations,
  getters,
};
